// ============================================================================
// GLOBAL USER HOOKS
// ============================================================================
// React hooks for Global User lookup (username check, tenant access)

import { useState, useEffect, useCallback } from 'react';
import { globalUserService } from 'api/services';
import type { GlobalUserResponse, UserTenantAccessRecord } from 'api/types';

// ============================================================================
// useCheckGlobalUser — debounced username existence check + auto-fill
// ============================================================================

interface GlobalUserCheckResult {
  exists: boolean;
  user: GlobalUserResponse | null;
  checking: boolean;
  error: string | null;
  check: (username: string) => Promise<void>;
  reset: () => void;
}

export const useCheckGlobalUser = (): GlobalUserCheckResult => {
  const [exists, setExists] = useState(false);
  const [user, setUser] = useState<GlobalUserResponse | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async (username: string) => {
    if (!username || username.trim().length < 2) {
      setExists(false);
      setUser(null);
      setError(null);
      return;
    }

    setChecking(true);
    setError(null);

    try {
      const existsResult = await globalUserService.exists(username.trim());
      if (existsResult.success && existsResult.data) {
        setExists(true);
        // Fetch full user details for auto-fill
        try {
          const userResult = await globalUserService.getByUsername(username.trim());
          if (userResult.success && userResult.data) {
            setUser(userResult.data);
          }
        } catch {
          // User exists but couldn't fetch details — that's OK
        }
      } else {
        setExists(false);
        setUser(null);
      }
    } catch {
      setExists(false);
      setUser(null);
      setError('Failed to check username');
    } finally {
      setChecking(false);
    }
  }, []);

  const reset = useCallback(() => {
    setExists(false);
    setUser(null);
    setChecking(false);
    setError(null);
  }, []);

  return { exists, user, checking, check, reset, error };
};

// ============================================================================
// useUserTenantAccess — fetch tenant access list for a user
// ============================================================================

interface UserTenantAccessResult {
  data: UserTenantAccessRecord[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useUserTenantAccess = (userId?: string): UserTenantAccessResult => {
  const [data, setData] = useState<UserTenantAccessRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAccess = useCallback(async () => {
    if (!userId) {
      setData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await globalUserService.getTenantAccess(userId);
      if (result.success) {
        setData(result.data || []);
      } else {
        setData([]);
      }
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  return { data, loading, error, refetch: fetchAccess };
};
